import { useState } from "react";
import { motion } from "framer-motion";
import { Trophy, Sparkles } from "lucide-react";
import { burst } from "@/lib/confetti";
import { sound } from "@/lib/audio";

const AWARDS = [
    {
        title: "The Lifetime Achievement Award",
        reason: "For Being Fabulous",
        note: "The committee reviewed the evidence and reached a unanimous verdict in record time. It wasn't close.",
    },
    {
        title: "CEO of Good Vibes & Big Dreams",
        reason: "Executive Excellence",
        note: "Corner office in the cosmos. Benefits include unlimited inspiration and a very forgiving meetings policy.",
    },
    {
        title: "The International Award",
        reason: "For Making Life More Interesting",
        note: "Presented annually to the person most likely to turn an ordinary Tuesday into a story worth retelling.",
    },
    {
        title: "The Golden Heart Award",
        reason: "For Being Someone Worth Appreciating",
        note: "Some awards are decided by judges. This one was decided by everyone who has ever met you.",
    },
    {
        title: "The Best Client Award",
        reason: "For Making a Website Developer's Professional Life Interesting",
        note: "And the award for Best Client goes to Mary Manzur! For outstanding contributions to the professional development of her website developer, including valuable feedback, unexpected revisions, and the occasional test of his problem-solving abilities.",
        plaque: "BEST CLIENT. DEVELOPER STILL RECOVERING.",
        aside: "Jokes aside, working with you has genuinely been a pleasure.",
        special: true,
    },
    {
        title: "The Lifetime Cake Privileges Award",
        reason: "Unlimited Cake, In Perpetuity",
        note: "Non-transferable, non-refundable, and legally binding in every known universe. Redeemable immediately.",
    },
];

export default function Awards({ reduce }) {
    const [revealed, setRevealed] = useState(() => new Set());
    const [accepted, setAccepted] = useState(false);

    const toggle = (i) => {
        sound.chime(659.25 + i * 60);
        setRevealed((prev) => {
            const next = new Set(prev);
            if (next.has(i)) next.delete(i);
            else next.add(i);
            return next;
        });
    };

    const accept = () => {
        if (accepted) return;
        setAccepted(true);
        sound.applause();
        const w = window.innerWidth;
        const h = window.innerHeight;
        burst({ x: w * 0.25, y: h * 0.3 });
        setTimeout(() => burst({ x: w * 0.75, y: h * 0.28, count: 70 }), 220);
        setTimeout(() => burst({ x: w * 0.5, y: h * 0.2, count: 110 }), 450);
    };

    return (
        <section data-testid="awards-section" className="relative z-[1] overflow-hidden px-6 py-24 sm:py-32">
            <div
                className="pointer-events-none absolute inset-0"
                aria-hidden="true"
                style={{
                    background:
                        "radial-gradient(ellipse 60% 40% at 20% 0%, rgba(243,208,133,0.13), transparent), radial-gradient(ellipse 60% 40% at 80% 0%, rgba(243,208,133,0.13), transparent), linear-gradient(180deg, rgba(42,20,56,0.5), transparent 30%)",
                }}
            />

            <div className="relative mx-auto max-w-5xl">
                <motion.div
                    className="text-center"
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                >
                    <p className="eyebrow-label mb-4">Act II — The Ceremony</p>
                    <h2 className="font-display text-4xl font-semibold sm:text-5xl lg:text-6xl">
                        The 2026 <span className="gold-text italic">Mary Awards</span>
                    </h2>
                    <p className="mx-auto mt-4 max-w-lg text-base font-light text-[#EDE7F8]/75 sm:text-lg">
                        Recognising excellence that the universe cannot adequately measure.
                        Tap each envelope of honour to hear the announcement.
                    </p>
                </motion.div>

                <div className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                    {AWARDS.map((a, i) => {
                        const open = revealed.has(i);
                        return (
                            <motion.button
                                key={a.title}
                                data-testid={`award-card-${i + 1}`}
                                onClick={() => toggle(i)}
                                aria-expanded={open}
                                className={`group relative overflow-hidden rounded-2xl border text-left transition-colors duration-500 ${
                                    open
                                        ? "border-[#F3D085]/60 bg-[#2A1438]/80"
                                        : "border-[#F3D085]/20 bg-[#160E2A]/75 hover:border-[#F3D085]/45"
                                } ${a.special ? "sm:col-span-2 lg:col-span-3" : ""}`}
                                initial={{ opacity: 0, y: 50 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-40px" }}
                                transition={{ duration: 0.7, delay: (i % 3) * 0.12, ease: [0.22, 1, 0.36, 1] }}
                                whileHover={reduce ? {} : { y: -6 }}
                            >
                                <div
                                    className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-[#F3D085] opacity-[0.06] blur-3xl transition-opacity duration-500 group-hover:opacity-[0.14]"
                                    aria-hidden="true"
                                />
                                <div className="relative p-6 sm:p-7">
                                    <div className="mb-5 flex items-center justify-between">
                                        <span className="font-mono2 text-[10px] tracking-[0.3em] text-[#F3D085]/60">
                                            AWARD Nº 0{i + 1}
                                        </span>
                                        <Trophy
                                            className={`h-5 w-5 transition-colors duration-500 ${open ? "text-[#FFD700]" : "text-[#F3D085]/40"}`}
                                        />
                                    </div>

                                    {!open ? (
                                        <div>
                                            <p className="font-display text-xl font-medium text-[#EDE7F8]/90 sm:text-2xl">
                                                {a.title}
                                            </p>
                                            <p className="mt-3 font-mono2 text-[10px] uppercase tracking-[0.28em] text-[#C4B5FD]/60">
                                                Sealed — tap to announce
                                            </p>
                                        </div>
                                    ) : (
                                        <motion.div
                                            initial={{ opacity: 0, y: 12 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.5 }}
                                        >
                                            <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#C4B5FD]/70">
                                                And the award goes to…
                                            </p>
                                            <p className="gold-text mt-2 font-display text-2xl font-semibold italic sm:text-3xl">
                                                Mary Manzur
                                            </p>
                                            <p className="mt-1 text-sm font-medium text-[#F3D085]/90">{a.reason}</p>
                                            <p className="mt-3 text-sm font-light leading-relaxed text-[#EDE7F8]/80">{a.note}</p>
                                            {a.plaque && (
                                                <div className="mt-5 inline-block rounded-lg border border-[#FFD700]/50 bg-[#0B0716]/60 px-4 py-2">
                                                    <span className="font-mono2 text-[11px] tracking-[0.2em] text-[#FFD700]">
                                                        {a.plaque}
                                                    </span>
                                                </div>
                                            )}
                                            {a.aside && (
                                                <p className="mt-4 border-l-2 border-[#F3D085]/50 pl-4 font-display text-lg italic text-[#FBF7EE]">
                                                    {a.aside}
                                                </p>
                                            )}
                                        </motion.div>
                                    )}
                                </div>
                            </motion.button>
                        );
                    })}
                </div>

                <motion.div
                    className="mt-14 text-center"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                >
                    <button data-testid="accept-awards-button" onClick={accept} className="btn-gold">
                        <Sparkles className="h-4 w-4" />
                        {accepted ? "Awards accepted — magnificent speech" : "Accept my awards"}
                    </button>
                    {accepted && (
                        <motion.p
                            data-testid="awards-accepted-message"
                            className="mt-5 font-display text-lg italic text-[#C4B5FD]"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                        >
                            "I'd like to thank the universe, the cake, and my long-suffering developer."
                        </motion.p>
                    )}
                </motion.div>
            </div>
        </section>
    );
}
